/** @fileoverview Multi-level tag filter with an inline source-assign bar. */
import { useState, useEffect, useMemo, useRef } from "react";
import { questionMatchesSteps, getNextEligibleFilters } from "../Components/TagFilterUtils";
import { buildTrie, traverseTrieMultiValue, collectNextValueIds } from "./helpers";
import { formatKey } from "../../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import Checkbox from "../../../globalComponents/ui/Checkbox";
import SourceAssignBar from "./SourceAssignBar";

/**
 * Progressive tag filter: pick a level-one value, then drill into further tag
 * levels, with an inline bar to assign a source to all matching KPIs.
 * @param {object} props Component props.
 * @returns {JSX.Element} The filter bar.
 */
export default function KpiFilterBar({
  allQuestions, filterSteps, onFilterStepsChange,
  pendingIds, disabled,
  sources, kpiSourceMap, onKpiSourceChange,
  onSelectFiltered
}) {
  const [openKey, setOpenKey] = useState(null);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpenKey(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => { setDropdownSearch(""); }, [openKey]);

  const activeSteps = useMemo(() => filterSteps.filter(s => s.values?.size), [filterSteps]);

  /**
   * Positional tag trie over all questions — the shared drill-down engine (also
   * used by SelectedKpisAccordion). Built once per dataset; dropdown enumeration
   * traverses it instead of re-scanning every question on each open.
   */
  const trie = useMemo(() => buildTrie(allQuestions), [allQuestions]);

  /**
   * Count of questions matching the full active filter path — invariant across
   * the crumb list, so computed once instead of per-crumb during render.
   */
  const matchCount = useMemo(
    () => allQuestions.filter(q => questionMatchesSteps(q, filterSteps)).length,
    [allQuestions, filterSteps],
  );

  const eligibleNextFilters = useMemo(() => getNextEligibleFilters(allQuestions, filterSteps), [allQuestions, filterSteps]);
  const nextKeys = useMemo(() => {
    const activeKeys = new Set(activeSteps.map(s => s.key));
    return eligibleNextFilters.filter(k => !activeKeys.has(k.key)).map(k => k.key);
  }, [eligibleNextFilters, activeSteps]);

  /**
   * Counts for the open dropdown, via one trie traversal instead of a full scan:
   * traverse the prefix (the active steps before the open key, each multi-value),
   * then for each next value tally total and selected questions. Distinct-per-value
   * because the trie's node ids are sets.
   * @type {Map<string, {total:number, selected:number}>}
   */
  const openValueCounts = useMemo(() => {
    const counts = new Map();
    if (!openKey) return counts;
    const stepIdx = activeSteps.findIndex(s => s.key === openKey);
    const stepsPrefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;
    const nodes = traverseTrieMultiValue(trie, stepsPrefix);
    for (const [value, ids] of collectNextValueIds(nodes, openKey).entries()) {
      let selected = 0;
      for (const id of ids) if (pendingIds?.has(id)) selected += 1;
      counts.set(value, { total: ids.size, selected });
    }
    return counts;
  }, [openKey, activeSteps, trie, pendingIds]);

  const openValues = useMemo(() => Array.from(openValueCounts.keys()).sort(), [openValueCounts]);

  const visibleValues = useMemo(() => {
    if (!dropdownSearch.trim()) return openValues;
    const q = dropdownSearch.toLowerCase();
    return openValues.filter(v => v.toLowerCase().includes(q));
  }, [openValues, dropdownSearch]);

  const openSelectedVals = useMemo(() => {
    const s = filterSteps.find(s => s.key === openKey);
    return s?.values ?? new Set();
  }, [filterSteps, openKey]);

  const openIsLevelOne = useMemo(() => {
    if (!openKey) return false;
    const stepIdx = activeSteps.findIndex(s => s.key === openKey);
    return stepIdx === 0 || (stepIdx === -1 && activeSteps.length === 0);
  }, [openKey, activeSteps]);

  const toggleValue = (key, value) => {
    if (disabled) return;
    if (openIsLevelOne) {
      const alreadySelected = openSelectedVals.has(value) && openSelectedVals.size === 1;
      const newSteps = alreadySelected ? [] : [{ key, values: new Set([value]) }];
      onFilterStepsChange(newSteps);
      if (!alreadySelected && onSelectFiltered) onSelectFiltered(newSteps);
      setOpenKey(null);
      return;
    }
    const next = filterSteps.map(s => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex(s => s.key === key);
    if (idx === -1) { next.push({ key, values: new Set([value]) }); }
    else {
      const vals = next[idx].values;
      if (vals.has(value)) vals.delete(value); else vals.add(value);
      if (!vals.size) { next.splice(idx, next.length - idx); setOpenKey(null); }
    }
    onFilterStepsChange(next);
    if (onSelectFiltered) onSelectFiltered(next);
  };

  const handleSelectAll = () => {
    if (disabled || openIsLevelOne) return;
    const allSel = visibleValues.every(v => openSelectedVals.has(v));
    const next = filterSteps.map(s => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex(s => s.key === openKey);
    if (allSel) {
      if (idx !== -1) {
        visibleValues.forEach(v => next[idx].values.delete(v));
        if (!next[idx].values.size) next.splice(idx, next.length - idx);
      }
    } else {
      if (idx === -1) next.push({ key: openKey, values: new Set(visibleValues) });
      else visibleValues.forEach(v => next[idx].values.add(v));
    }
    onFilterStepsChange(next);
    if (onSelectFiltered) onSelectFiltered(next);
  };

  const removeStep = (key) => {
    if (disabled) return;
    const idx = filterSteps.findIndex(s => s.key === key);
    if (idx >= 0) {
      const newSteps = filterSteps.slice(0, idx);
      onFilterStepsChange(newSteps);
      if (onSelectFiltered) onSelectFiltered(newSteps);
      setOpenKey(null);
    }
  };

  const handleOpenKey = (key) => {
    if (disabled) return;
    setOpenKey(prev => prev === key ? null : key);
  };

  const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => openSelectedVals.has(v));

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--es-slate-400)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Filter:</span>

        {activeSteps.map((step, i) => {
          const isOpen = openKey === step.key;

          return (
            <span key={step.key} style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
              {i > 0 && <span style={{ color: "var(--es-sky-200)", fontSize: 15, lineHeight: 1 }}>›</span>}
              <button
                onClick={() => handleOpenKey(step.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "5px 8px 5px 10px", borderRadius: 7,
                  border: `1.5px solid ${isOpen ? "var(--es-blue-500)" : "var(--es-blue-300)"}`,
                  background: isOpen ? "var(--es-blue-50)" : "var(--es-blue-100)",
                  fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer",
                  color: "var(--es-blue-700)", transition: "all 0.15s"
                }}
              >
                <span style={{ color: "var(--es-blue-500)", fontWeight: 700 }}>{formatKey(step.key)}:</span>
                <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {Array.from(step.values).join(", ")}
                </span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 8, background: "var(--es-blue-200)", color: "var(--es-blue-800)", marginLeft: 2 }}>{matchCount}</span>
                <span style={{ fontSize: 9, color: "var(--es-blue-300)" }}>▾</span>
                {!disabled && (
                  <span
                    className="es-chip-x"
                    onClick={e => { e.stopPropagation(); removeStep(step.key); }}
                    style={{ fontSize: 13, lineHeight: 1, fontWeight: 900, marginLeft: 1, padding: "0 1px" }}
                  >×</span>
                )}
              </button>
            </span>
          );
        })}

        {activeSteps.length > 0 && nextKeys.length > 0 && (
          <span style={{ color: "var(--es-slate-200)", fontSize: 15, lineHeight: 1 }}>›</span>
        )}

        {nextKeys.map(key => {
          const isOpen = openKey === key;
          return (
            <button key={key} onClick={() => handleOpenKey(key)} style={{
              padding: "5px 12px", borderRadius: 7,
              border: `1.5px solid ${isOpen ? "var(--es-blue-300)" : "var(--es-slate-200)"}`,
              background: isOpen ? "var(--es-blue-50)" : "var(--es-white)",
              fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer",
              color: isOpen ? "var(--es-blue-600)" : "var(--es-slate-500)", transition: "all 0.15s"
            }}>
              {formatKey(key)} ▾
            </button>
          );
        })}

        {activeSteps.length > 0 && !disabled && (
          <button onClick={() => { onFilterStepsChange([]); if (onSelectFiltered) onSelectFiltered([]); setOpenKey(null); }}
            style={{ fontSize: 10, color: "var(--es-red-500)", border: "none", background: "none", cursor: "pointer", fontWeight: 700, marginLeft: 4 }}>
            ✕ Clear
          </button>
        )}
      </div>

      <SourceAssignBar
        sources={sources}
        filterSteps={filterSteps}
        allQuestions={allQuestions}
        kpiSourceMap={kpiSourceMap}
        onKpiSourceChange={onKpiSourceChange}
        disabled={disabled}
      />

      {openKey && openValues.length > 0 && !disabled && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 999,
          background: "var(--es-white)", border: "1px solid var(--es-slate-200)", borderRadius: 10,
          boxShadow: "0 8px 24px var(--es-shadow-md)", overflow: "hidden",
          width: 300, maxHeight: 360, display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "7px 12px", background: "var(--es-slate-50)", borderBottom: "1px solid var(--es-slate-100)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: "var(--es-slate-600)", textTransform: "uppercase", letterSpacing: "0.6px" }}>{formatKey(openKey)}</span>
            <span style={{ fontSize: 9, color: "var(--es-slate-400)", fontWeight: 600 }}>{openIsLevelOne ? "select one" : "multi-select"}</span>
          </div>

          {!openIsLevelOne && (
            <div style={{ padding: "6px 10px", borderBottom: "1px solid var(--es-slate-100)", flexShrink: 0 }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--es-slate-400)", pointerEvents: "none" }}>⌕</span>
                <input
                  className="es-input"
                  value={dropdownSearch}
                  onChange={e => setDropdownSearch(e.target.value)}
                  placeholder={`Search ${formatKey(openKey)}…`}
                  style={{ width: "100%", fontSize: 11, borderRadius: 6, padding: "5px 8px 5px 24px", background: "var(--es-white)", color: "var(--es-slate-800)", outline: "none" }}
                />
                {dropdownSearch && (
                  <span onClick={() => setDropdownSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--es-slate-400)", cursor: "pointer" }}>×</span>
                )}
              </div>
            </div>
          )}

          {!openIsLevelOne && (
            <div style={{ padding: "5px 12px", borderBottom: "1px solid var(--es-slate-100)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "var(--es-gray-50)" }}>
              <span style={{ fontSize: 11, color: "var(--es-slate-500)" }}>
                {openSelectedVals.size} of {openValues.length} selected {dropdownSearch && visibleValues.length !== openValues.length && `(${visibleValues.length} shown)`}
              </span>
              <button onClick={handleSelectAll} style={{ fontSize: 11, fontWeight: 600, color: allVisibleSelected ? "var(--es-red-600)" : "var(--es-cyan-700)", background: "none", border: "none", cursor: "pointer", padding: "2px 0" }}>
                {allVisibleSelected ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}

          <div style={{ overflowY: "auto", flex: 1 }}>
            {visibleValues.length === 0 ? (
              <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 11, color: "var(--es-slate-400)" }}>No options match your search.</div>
            ) : (
              visibleValues.map(val => {
                const { total, selected } = openValueCounts.get(val) ?? { total: 0, selected: 0 };
                const isChecked = openSelectedVals.has(val);
                return (
                  <div key={val} onClick={() => toggleValue(openKey, val)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", background: isChecked ? "var(--es-sky-50)" : "transparent", borderBottom: "1px solid var(--es-slate-50)", transition: "background 0.1s" }}>
                    {openIsLevelOne ? (
                      <span style={{
                        width: 17, height: 17, borderRadius: "50%", flexShrink: 0,
                        border: `1.5px solid ${isChecked ? "var(--es-accent)" : "var(--es-slate-300)"}`,
                        background: isChecked ? "var(--es-accent)" : "var(--es-white)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.15s", boxShadow: isChecked ? "0 1px 3px var(--es-accent-shadow)" : "none"
                      }}>
                        {isChecked && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--es-white)", display: "block" }} />}
                      </span>
                    ) : (
                      <Checkbox checked={isChecked} onChange={() => toggleValue(openKey, val)} />
                    )}
                    <span style={{ flex: 1, fontSize: 12, color: "var(--es-gray-700)", fontWeight: isChecked ? 600 : 400 }}>{val}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, background: isChecked ? "var(--es-blue-100)" : "var(--es-slate-100)",
                      color: isChecked ? "var(--es-blue-700)" : "var(--es-slate-500)", borderRadius: 10, padding: "2px 8px",
                      border: `1.5px solid ${isChecked ? "var(--es-blue-200)" : "var(--es-slate-200)"}`, whiteSpace: "nowrap"
                    }}>
                      {openIsLevelOne ? total : `${selected}/${total}`}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
